import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import Fieldset from '@/components-latest/layout/Fieldset'
import Separator from '@/components-latest/primitives/Separator'
import FiltersDrawer from '@/components-latest/shared/FiltersDrawer'
import {
  DatePickerField,
  Form,
  GroupCheckboxField,
  GroupToggleField,
  NumericField
} from '@/components-latest/shared/Form'
import { useOrdersFilterForm } from '@/components-latest/ui/OrdersPage/OrdersFilter/useOrdersFilterForm'
import { STORE_BRAND_FILTER_OPTIONS } from '@/ducks/orders/helpers/brand.helpers'
import { selectCurrencyCode } from '@/ducks/store/selectors/store.selectors'

import Styles from './OrdersFilter.module.scss'

interface IOrdersFilterProps {
  children: React.ReactNode
}

const OrdersFilter: React.FC<IOrdersFilterProps> = ({ children }) => {
  const { t } = useTranslation()

  const currency = useSelector(selectCurrencyCode)
  const [shouldResetForm, setShouldResetForm] = useState(false)

  const {
    onReset,
    onSubmit,
    shouldDisableSubmit,
    form,
    control,
    startDate,
    endDate,
    shouldDisableReset
  } = useOrdersFilterForm(shouldResetForm)

  return (
    <FiltersDrawer
      onOpenChange={isOpen => setShouldResetForm(isOpen)}
      onReset={onReset}
      disableReset={shouldDisableReset}
      onSubmit={onSubmit}
      disabled={shouldDisableSubmit}
      trigger={children}
      data-testid="orders-filters-drawer">
      <Form {...form}>
        <form className={Styles.form} onSubmit={onSubmit}>
          <Fieldset label={t('common.date-range')}>
            <div className={Styles.dateRangeRow}>
              <DatePickerField
                control={control}
                name="start_date"
                maxDate={endDate || new Date()}
                placeholder={t('orders.filters.start-date.placeholder')}
              />
              <DatePickerField
                control={control}
                name="end_date"
                minDate={startDate}
                maxDate={new Date()}
                placeholder={t('orders.filters.end-date.placeholder')}
              />
            </div>
            <Separator />
          </Fieldset>
          <Fieldset label={t('orders.price-range', { currency })}>
            <div className={Styles.priceRangeRow}>
              <NumericField
                control={form.control}
                name="start_price"
                {...({ placeholder: t('common.min') } as any)}
              />
              <NumericField
                control={form.control}
                name="end_price"
                {...({ placeholder: t('common.max') } as any)}
              />
            </div>
            <Separator />
          </Fieldset>
          <Fieldset label={t('orders.brand-filter-label')}>
            <div className={Styles.brandRow}>
              {STORE_BRAND_FILTER_OPTIONS.map(({ value, labelKey }) => (
                <GroupToggleField
                  key={value || 'all'}
                  control={form.control}
                  name="brand"
                  value={value}
                  label={t(labelKey)}
                />
              ))}
            </div>
            <Separator />
          </Fieldset>
          <Fieldset label={t('orders.statuses.label')}>
            <GroupCheckboxField
              control={form.control}
              name="statuses"
              value="COMPLETED"
              label={t('orders.statuses.completed')}
            />
            <GroupCheckboxField
              control={form.control}
              name="statuses"
              value="PICKED_UP"
              label={t('orders.statuses.picked-up')}
            />
            <GroupCheckboxField
              control={form.control}
              name="statuses"
              value="REJECTED"
              label={t('orders.statuses.rejected')}
            />
            <GroupCheckboxField
              control={form.control}
              name="statuses"
              value="CANCELED"
              label={t('orders.statuses.canceled')}
            />
            <GroupCheckboxField
              control={form.control}
              name="statuses"
              value="RETURNED"
              label={t('orders.statuses.returned')}
            />
            <GroupCheckboxField
              control={form.control}
              name="statuses"
              value="TIMED_OUT"
              label={t('orders.statuses.timed-out')}
            />
          </Fieldset>
        </form>
      </Form>
    </FiltersDrawer>
  )
}

export default OrdersFilter
